<template>
  <v-card v-if="cashout">
    <suspicion-details :is-active.sync="showSuspicionInfo" :details="getSuspicionDetail" />
    <bypass-comment
      :is-active.sync="showBypassComment"
      :transaction="cashout"
      @commented="bypassCommented"
    />
    <rejection-comment
      :is-active.sync="showRejectionComment"
      :transaction="cashout"
      @commented="rejectCommented"
    />
    <send-code-form
      :is-active.sync="isSendingCode"
      :cashout="cashout"
      @changed="withdrawCodeSent"
    />
    <v-toolbar color="grey lighten-4" light tile flat>
      <v-toolbar-title centered class="headline font-weight-bold">
        Transaction Details
      </v-toolbar-title>
      <div class="flex-grow-1"></div>

      <ReindexButton :paymentId="cashout.payout.id" />

      <v-btn
        color="primary"
        v-if="directPaymentTransactionId"
        class="mr-3"
        @click="goToDirectTransfert"
      >
        Go to Transfer
      </v-btn>

      <v-btn
        color="primary"
        class="mr-3"
        v-if="cashout.status == 'initiated'"
        @click="isSendingCode = true"
        :disabled="cashout.status !== 'initiated'"
      >
        <v-icon color="light-blue lighten-4">mdi-alert-circle-check</v-icon>
        Confirm OTP
      </v-btn>

      <v-btn
        color="primary"
        class="mr-3"
        v-if="cashout.status == 'initiated'"
        @click="resendCashoutCode"
        :disabled="cashout.status !== 'initiated'"
      >
        <v-icon color="light-blue lighten-4">mdi-alert-circle-check</v-icon>
        Resend OTP Code
      </v-btn>

      <div v-if="cashout.done_at != null">
        <v-btn color="primary" :disabled="cashout.done_at == null" @click="downloadInvoice">
          <v-icon> mdi-download </v-icon>
          Download receipt
        </v-btn>
      </div>

      <v-btn
        color="primary"
        class="mr-3"
        v-if="cashout.status == 'verified'"
        @click="validatePayout"
        :disabled="cashout.status !== 'verified'"
      >
        <v-icon color="light-blue lighten-4">mdi-alert-circle-check</v-icon>
        Validate Payout
      </v-btn>

      <v-btn
        color="primary"
        class="mr-3"
        v-if="cashoutIsStuckSuspicious"
        @click="bypassCashout"
        :disabled="isArchived"
      >
        <v-icon color="light-blue lighten-4">mdi-alert-circle-check</v-icon>
        Bypass Suspicion
      </v-btn>

      <v-btn
        color="primary"
        class="mr-3"
        v-if="cashoutIsStuckSuspicious"
        @click="rejectCashout"
        :loading="isRejecting"
        :disabled="isArchived"
      >
        <v-icon color="light-blue lighten-4">mdi-close-circle</v-icon>
        Reject Payout
      </v-btn>

      <v-btn
        color="primary"
        class="mr-3"
        v-if="cashout.status == 'validated'"
        :disabled="cashout.status !== 'validated'"
        @click="startPayout"
      >
        <v-icon color="light-blue lighten-4">mdi-reload</v-icon>
        Start Payout
      </v-btn>

      <v-btn
        color="primary"
        class="mr-3"
        :loading="isArchiving"
        v-if="cashout.status == 'completed' && cashout.payout.status == 'started'"
        :disabled="cashout.status !== 'completed' && cashout.payout.status !== 'started'"
        @click="completePayout"
      >
        <v-icon color="light-blue lighten-4">mdi-timer-sand-complete</v-icon>
        Complete Payout
      </v-btn>

      <template
        v-if="cashoutIsStuck"
        class="mr-3"
        color="primary"
        :loading="isRetrying"
        :disabled="isArchived"
      >
        <div class="text-center">
          <v-menu open-on-hover bottom offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="primary" dark v-bind="attrs" v-on="on">
                <v-icon color="light-blue lighten-4">mdi-sync</v-icon>
                Retry Payout
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-for="(item, index) in alternative_payout_methods" :key="index">
                <v-list-item-title class="v-list-item--link" @click="retryCashoutWithMethod(item)">
                  {{ item.name }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>

      <v-btn color="primary" :loading="isArchiving" :disabled="isArchived" @click="archiveCashout">
        <v-icon color="light-blue lighten-4">mdi-archive</v-icon>
        Archive Payout
      </v-btn>
      <v-btn color="primary ml-3" :loading="isRestoring" v-if="isArchived" @click="restoreCashout">
        <v-icon color="light-blue lighten-4">mdi-restore</v-icon>
        Restore
      </v-btn>
    </v-toolbar>

    <!-- cashout info -->
    <v-divider />
    <v-card-text class="pb-0 pt-0">
      <v-row>
        <v-col cols="12" md="2">
          <h2 class="subtitle-2 text-uppercase">General</h2>
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="Amount" :value="transactionKey('amount_without_fee')" type="date">
            €{{ transactionKey("amount_without_fee") | currency }}
          </key-value>
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="Fees" :value="transactionKey('applied_fee')" type="date">
            €{{ transactionKey("applied_fee") }}
          </key-value>
        </v-col>
        <v-col cols="12" md="3">
          <key-value title="Exchange rate (with spread)">
            <span class="font-weight-bold primary--text title">
              {{ transactionKey("exchange_rate") }}  ({{ transactionKey("exchange_rate_with_spread") }})
            </span>
          </key-value>
        </v-col>

        <v-col cols="12" md="2">
          <key-value title="Local Amount">
            <span class="font-weight-bold primary--text title">
              {{ transactionKey("local_amount_without_fee") | currency
              }}{{ transactionKey("local_currency") }}
            </span>
          </key-value>
        </v-col>
      </v-row>
    </v-card-text>
    <v-divider />
    <v-card-text class="pb-0 pt-0">
      <v-row>
        <v-col cols="12" md="2">
          <h2 class="subtitle-2 text-uppercase">Cashout</h2>
        </v-col>
        <v-col cols="12" md="4">
          <key-value title="Id" :value="transactionKey('id')" type="date" />
        </v-col>
        <v-col cols="12" md="3">
          <key-value title="Started At" :value="transactionKey('started_at')" type="date" />
        </v-col>
        <v-col cols="12" md="3">
          <key-value title="Done At" :value="transactionKey('done_at')" type="date" />
        </v-col>
      </v-row>
    </v-card-text>
    <v-divider />
    <v-card-text class="pb-0 pt-0">
      <v-row>
        <v-col cols="12" md="2">
          <h2 class="subtitle-2 text-uppercase">Payout</h2>
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="Id" :value="transactionKey('payout.id')" type="date" />
        </v-col>
        <v-col cols="12" md="2">
          <key-value
            title="Payout Start Date"
            :value="transactionKey('payout.started_at')"
            type="date"
          />
        </v-col>
        <v-col cols="12" md="2">
          <key-value
            title="Payout Done Date"
            :value="transactionKey('payout.done_at')"
            type="date"
          />
        </v-col>
        <v-col cols="12" md="1">
          <key-value title="Status" :value="transactionKey('compiled_status')" type="date" />
          <v-icon
            color="warning"
            v-if="transactionKey('status') == 'suspicious'"
            @click="suspicionInfo"
            >mdi-information-outline
          </v-icon>
        </v-col>
        <v-col cols="12" md="1">
          <key-value
            title="Payout method Id"
            :value="transactionKey('payout.back_reference')"
          />
        </v-col>
        <v-col cols="12" md="2">
          <template class="pb-0 pt-0">
            <key-value
            title="Payout method name"
            :value="payoutMethodName"
           />
          </template>
        </v-col>
      </v-row>
    </v-card-text>
    <v-divider />
    <v-card-text v-if="bankAccount" class="pb-0 pt-0">
      <v-row no-gutters>
        <v-col cols="12" md="2" aria-rowspan="2">
          <h2 class="subtitle-2 text-uppercase">Bank Account Details</h2>
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="Title" :value="bankAccount.title" type="date" />
        </v-col>
        <v-col cols="12" md="1">
          <key-value title="bank name" :value="bankAccount.bank_name" type="date" />
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="Owner name" :value="bankAccount.owner_name" type="date" />
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="IBAN" :value="bankAccount.iban" type="date" />
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="BIC" :value="bankAccount.bic" type="date" />
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-text v-if="mobileAccount" class="pb-0 pt-0">
      <v-row>
        <v-col cols="12" md="2" aria-rowspan="2">
          <h2 class="subtitle-2 text-uppercase">Mobile Account Details</h2>
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="Title" :value="mobileAccount.title" />
        </v-col>
        <v-col cols="12" md="1">
          <key-value title="Operator" :value="mobileAccount.operator" />
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="Owner name" :value="mobileAccount.owner_name" />
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="Phone Number" :value="mobileAccount.phone_number" />
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="Country" :value="mobileAccount.country" />
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-text v-if="mfiAccount" class="pb-0 pt-0">
      <v-row no-gutters>
        <v-col cols="12" md="2" aria-rowspan="2">
          <h2 class="subtitle-2 text-uppercase">MFI Account Details</h2>
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="Title" :value="mfiAccount.title" />
        </v-col>
        <v-col cols="12" md="2">
          <key-value title="MFI name" :value="mfiAccount.mfi_name" />
        </v-col>
        <v-col cols="12" md="3">
          <key-value title="Owner name" :value="mfiAccount.owner_name" />
        </v-col>
        <v-col cols="12" md="3">
          <key-value title="Account number" :value="mfiAccount.owner_id" />
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-text v-if="deliveryAccount" class="pb-0 pt-0">
      <v-row no-gutters>
        <v-col cols="12" md="2" aria-rowspan="2">
          <h2 class="subtitle-2 text-uppercase">Deliver Account Details</h2>
        </v-col>
        <v-col cols="12" md="3">
          <key-value title="Receiver fullname" :value="deliveryAccount.fullname" />
        </v-col>
        <v-col cols="12" md="3">
          <key-value title="Receiver Phonenumber" :value="deliveryAccount.phone_number" />
        </v-col>
        <v-col cols="12" md="3">
          <key-value title="Receiver Country" :value="deliveryAccount.country" />
        </v-col>
      </v-row>
    </v-card-text>
    <v-divider />
    <v-card-text v-if="cashout.payout.data.failure_reasons">
      <v-row no-gutters>
        <v-col cols="12" md="12" aria-rowspan="12">
          <h2 class="subtitle-2 text-uppercase">Payout Failure Reasons</h2>
          <v-data-table
            title="Payout Failure Reasons"
            :items="cashout.payout.data.failure_reasons"
            :headers="failureReasonsHeader"
            ref="table"
            class="elevation-0"
            :single-expand="false"
            show-expand
            :expanded.sync="expanded"
          >
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length">
                <pre>{{ item.payload }}</pre>
              </td>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card-text>
    <v-divider />
    <update-reason
      :data.sync="cashout.history"
      title="Cashout Activity"
      class="mt-2"
      sortBy="id"
      sortOrder="desc"
    />
  </v-card>
</template>
<script>
import ReindexButton from "../../../components/forms/ReindexButton.vue";
import sendCodeForm from "./sendCodeForm";
import UpdateReason from "@/app/updateReason/show";
export default {
  components: {
    ReindexButton,
    sendCodeForm,
    SuspicionDetails: require("../../payins/suspicionDetails").default,
    UpdateReason,
    BypassComment: require("../../payins/bypassComment").default,
    RejectionComment: require("../../payins/rejectionComment").default,
  },
  props: {
    dataTransaction: {
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      isSendingCode: false,
      expanded: [],
      directPaymentTransactionId: this.dataTransaction.direct_payment?.transaction_id,
      cashout: this.dataTransaction,
      isArchived: this.dataTransaction.isArchived || false,
      bankAccount: null,
      mobileAccount: null,
      mfiAccount: null,
      deliveryAccount: null,
      isArchiving: false,
      isRestoring: false,
      isRetrying: false,
      isRetryingSuspicious: false,
      showSuspicionInfo: false,
      alternative_payout_methods: this.dataTransaction.alternative_payout_methods,
      isRejecting: false,
      showRejectionComment: false,
      showBypassComment: false,
      failureReasonsHeader: [
        {
          text: "Tag",
          align: "start",
          sortable: false,
          value: "tag",
        },
        {
          text: "Type",
          align: "start",
          sortable: false,
          value: "type",
        },
        {
          text: "Code",
          align: "start",
          sortable: false,
          value: "code",
        },
        {
          text: "Content",
          align: "start",
          sortable: false,
          value: "content",
        },
        { text: "", value: "data-table-expand" },
      ],
    };
  },
  computed: {
    payoutMethodName() { 
      if (this.bankAccount) 
        return 'Bank'
      if (this.mobileAccount) 
        return 'Mobile Money'
      if (this.mfiAccount)
        return 'Mfi'
      if (this.deliveryAccount)
        return 'Delivery'
      return 'Undefined'
    },
    cashoutIsStuck() {
      const status = this.transactionKey("status");
      if (
        this.alternative_payout_methods.length > 0 &&
        (status === "error" || this.cashout.payout.status === "failed" || status === "started")
      ) {
        return true;
      }
      return false;
    },
    cashoutIsStuckSuspicious() {
      const status = this.transactionKey("status");
      if (status === "suspicious") {
        return true;
      }
      return false;
    },
    getSuspicionDetail() {
      const getSuspicionKey = (data, key) => {
        let obj = {};
        for (let k of data.RegistreDetail) {
          for (let k2 of k.Valeur) {
            for (let k3 in k2) {
              obj[k3] = k2[k3];
            }
          }
        }
        if (key in obj) {
          return obj[key];
        }
      };
      let reasons = [];
      if (this.cashout.source_client.criminal_cases.length > 0) {
        let source_motif = JSON.parse(this.cashout.source_client.criminal_cases[0].data);
        let parsed_source_motif = getSuspicionKey(source_motif, "Motifs");
        let str_source_motif = `${this.cashout.source_user.full_name} : ${parsed_source_motif}`;
        reasons.push(str_source_motif);
      }
      if (this.cashout.destination_client.criminal_cases.length > 0) {
        let destination_motif = JSON.parse(this.cashout.destination_client.criminal_cases[0].data);
        let parsed_destination_motif = getSuspicionKey(destination_motif, "Motifs");
        let str_destination_motif = `${this.cashout.destination_user.full_name} : ${parsed_destination_motif}`;
        reasons.push(str_destination_motif);
      }
      return reasons.join("\n");
    },
  },
  created() {
    if (this.cashout.payout.data.bank_account_id) {
      const id = this.cashout.payout.data.bank_account_id;
      this.$http
        .get(`/api/v1/userBankAccounts/${id}`)
        .then((account) => (this.bankAccount = account))
        .catch((error) => Notify.error(error.message));
    } else if (this.cashout.payout.data.mobile_money_account_id) {
      const id = this.cashout.payout.data.mobile_money_account_id;
      this.$http
        .get(`/api/v1/userMMAccounts/${id}`)
        .then((account) => (this.mobileAccount = account))
        .catch((error) => Notify.error(error.message));
    } else if (this.cashout.payout.data.mfi_account_id) {
      const id = this.cashout.payout.data.mfi_account_id;
      this.$http
        .get(`/api/v1/userMFIAccounts/${id}`)
        .then((account) => (this.mfiAccount = account))
        .catch((error) => Notify.error(error.message));
    } else if (this.cashout.payout.data.delivery_account_id) {
      const id = this.cashout.payout.data.delivery_account_id;
      this.$http
        .get(`/api/v1/userDeliveryAccounts/${id}`)
        .then((account) => (this.deliveryAccount = account))
        .catch((error) => Notify.error(error.message));
    }
  },
  methods: {
    goToDirectTransfert() {
      this.$router.push({name:"transfers.show", params: { id:this.directPaymentTransactionId }});
    },
    suspicionInfo() {
      this.showSuspicionInfo = true;
    },
    withdrawCodeSent({ message }) {
      this.isSendingCode = false;
      Notify.success(message);
      this.$emit("reloadCashout");
    },
    downloadInvoice() {
      this.processing = true;
      this.$http
        .get(`api/v1/payments/invoices/${this.cashout.payout.id}`, { responseType: "blob" })
        .then((response) => this.convertResponse(response))
        .catch((error) => Notify.errorFromResponse(error, { close: true }))
        .finally(() => {
          this.processing = false;
        });
    },
    convertResponse(response) {
      const blob = new Blob([response], { type: response.type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "invoice-" + this.cashout.id);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    transactionKey(key, defaultValue = null) {
      return this.extractDataKey(this.cashout, key, defaultValue);
    },
    async validatePayout() {
      const id = this.cashout.id;
      await Notify.confirm(`Is this payout valid?`).then(() => {
        this.$http
          .post(`/api/v1/validatePayout/${id}`)
          .then((result) => {
            Notify.success(result.message);
            this.$emit("reloadCashout");
          })
          // eslint-disable-next-line no-unused-vars
          .catch((error) => {
            Notify.error(error.message);
          });
      });
    },
    async rejectPayout() {
      const id = this.cashout.id;
      await Notify.confirm(`You want to reject this payout?`).then(() => {
        this.$http
          .post(`/api/v1/rejectPayout/${id}`)
          .then((result) => {
            Notify.success(result.message);
            this.$emit("reloadCashout");
          })
          // eslint-disable-next-line no-unused-vars
          .catch((error) => {
            Notify.error(error.message);
          });
      });
    },
    async resendCashoutCode() {
      const id = this.cashout.id;
      await Notify.confirm(`Do you want to resend Cashout Code?`).then(() => {
        this.$http
          .post(`/api/v1/cashout/${id}/resendCode`)
          .then((result) => {
            Notify.success(result.message);
            this.$emit("reloadCashout");
          })
          // eslint-disable-next-line no-unused-vars
          .catch((error) => {
            Notify.error(error.message);
          });
      });
    },
    async startPayout() {
      const id = this.cashout.id;
      await Notify.confirm(`Confirm to start this payout`).then(() => {
        this.$http
          .post(`/api/v1/startPayout/${id}`)
          .then((result) => {
            Notify.success(result.message);
            this.$emit("reloadCashout");
          })
          // eslint-disable-next-line no-unused-vars
          .catch((error) => {
            Notify.error(error.message);
          });
      });
    },
    completePayout() {
      const id = this.cashout.id;
      Notify.confirm(`Confirm to complete this Payout`).then(() => {
        this.$http
          .post(`/api/v1/completePayoutPayment/${id}`)
          .then((result) => {
            Notify.success(result.message);
            this.$emit("reloadCashout");
          })
          // eslint-disable-next-line no-unused-vars
          .catch((error) => {
            Notify.error(error.message);
          });
      });
    },
    archiveCashout() {
      const id = this.cashout.id;
      if (!id) {
        return;
      }
      Notify.confirm(`Archieving the cashout will hide it from the list of transactions`).then(
        () => {
          this.isArchiving = true;
          this.$http
            .delete(`/api/v1/cashout/archive/${id}`)
            .then(() => {
              Notify.success("Transaction was archived successfully!");
              this.$router.push({ name: "cashouts.index" });
            })
            // eslint-disable-next-line no-unused-vars
            .catch((error) => {
              // console.error(error);
              this.archived = false;
              this.isArchiving = false;
              Notify.error("Transaction was not archived successfully. Please try again!");
            });
        },
      );
    },
    rejectCashout() {
      this.showRejectionComment = true;
    },
    bypassCashout() {
      this.showBypassComment = true;
    },
    restoreCashout() {
      this.isRestoring = true;
      this.$http
        .put(`api/v1/cashout/restore/${this.transactionKey("id")}`)
        .then(({ message }) => {
          Notify.success(message);
          this.$router.push({ name: "cashouts.index" });
        })
        .catch((error) => {
          Notify.errorFromResponse(error, { close: true });
        })
        .finally(() => (this.isRestoring = false));
    },
    retryCashout() {
      this.isRetrying = true;
      this.$http
        .get(`api/v1/transactions/withdraw/retry/${this.transactionKey("payout.transaction_id")}`)
        .then(({ status }) => {
          Notify.success(status);
          this.$router.push({ name: "cashouts.index" });
        })
        .catch((error) => {
          Notify.errorFromResponse(error, { close: true });
        })
        .finally(() => (this.isRetrying = false));
    },
    retryCashoutWithMethod(payoutMethod) {
      this.isRetrying = true;
      this.$http
        .get(
          `api/v1/transactions/withdraw/retry/${
            this.transactionKey("payout.transaction_id") + "/" + payoutMethod.id
          }`,
        )
        .then(({ status }) => {
          Notify.success(status);
          this.$router.push({ name: "cashouts.index" });
        })
        .catch((error) => {
          Notify.errorFromResponse(error, { close: true });
        })
        .finally(() => (this.isRetrying = false));
    },
    async retrySuspiciousCashout() {
      this.isRetryingSuspicious = true;
      await Notify.confirm(
        `You are about to validate the withdraw. It will be executed automatically.`,
      ).then(() => {
        this.$http
          .post(
            `api/v1/transactions/bypass-suspicion/${this.transactionKey("payout.transaction_id")}`,
          )
          .then((result) => {
            Notify.success(result.message);
            this.$emit("reloadCashout");
          })
          // eslint-disable-next-line no-unused-vars
          .catch((error) => {
            Notify.error(error.message);
          });
      });
    },
    bypassCommented({ message }) {
      Notify.success(message);
      this.$router.push({ name: "cashouts.index" });
    },
    rejectCommented({ message }) {
      Notify.success(message);
      this.$router.push({ name: "cashouts.index" });
    },
  },
};
</script>
